import React, {
  useState, useEffect, useRef, useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import moment from 'moment-timezone';
import _ from 'lodash';
import { useOutletContext } from 'react-router';
import { DateObject } from 'react-multi-date-picker';
import ReactApexChart from 'react-apexcharts';
import { Skeleton } from '@mui/material';
import { useApiData } from '../../../config/reducer/Modules';
import {
  APIGetDevices,
  APIGetV3FuelManagement,
  APIGetV3FuelLevelSignal,
  APIGetActiveEvTariffs,
  APIGetCurrentFuelPrices,
  APIGetFuelPriceHistory,
} from '../../../config/restAPI/FetchAPI';
import FuelLevelSignalPanel from './FuelLevelSignalPanel';
import FleetEventTimelinePanel from './FleetEventTimelinePanel';
import {
  buildFuelLevelSignalView,
  buildFleetEventTimelineView,
  buildFuelLevelSignalCacheKey,
  normalizeApiFuelSourceKey,
  normalizeTelemetrySourceKey,
  readFuelLevelSignalCache,
  resolveFuelSignalApiPayload,
  writeFuelLevelSignalCache,
} from './fuelLevelSignalUtils';
import ListNavigationTab from '../../../component/listNavigationTab/ListNavigationTab';
import DateRangePicker from '../../../component/dateRangePicker/DateRangePicker';
import Autocomplete from '../../../component/autocomplete/Autocomplete';
import Card from '../../../component/card/Card';
import {
  FLEET_FILTER,
  FLEET_FILTER_OPTIONS,
  buildAssetMetaMap,
  mergeTableRowMetaIntoMap,
  lookupAssetMeta,
  buildTariffRateMap,
  buildFuelPriceMap,
  enrichRowWithCosts,
  isFuelClass,
  isEnergyClass,
  sumField,
  hasIncompleteIdleWasteSpec,
  resolveAssetFuelTypes,
  resolveApiChargingTariff,
  resolveTotalEnergyKpi,
  enrichEnergyRowsWithAggregate,
  recalculateEnergyRowMetrics,
  formatSubtotalCostRM,
  buildDailyFuelConsumptionByAsset,
  buildDailyFuelEfficiencyByAsset,
  buildPerAssetFuelEfficiencySeriesFromPayloads,
  buildPerAssetFuelSeriesFromPayloads,
  classifyFleet,
  resolveSectionVisibility,
  DAILY_FUEL_ASSET_COLORS,
  buildFuelPriceHistoryQuery,
  extractPerAssetFuelEfficiencySeriesFromResponse,
  extractPerAssetFuelSeriesFromResponse,
} from './fuelCostUtils';
import {
  extractEvTariffData,
  normalizeEvTariffRows,
  getActiveCurrentEvTariffRows,
} from '../../inventory/priceRegistry/evTariffMapper';
import {
  AGENCY_TIMEZONE_COOKIE,
  resolveAgencyTimezone,
} from '../../../config/function/agencyTimezone';
import {
  buildAgencyRequestDayRange,
  buildAgencyUtcDayRange,
  resolveFuelResponseTimezone,
  toCivilDateKey,
} from './fuelRequestTimeUtils';
import {
  buildFuelPriceRows,
  extractFuelPriceData,
} from '../../inventory/priceRegistry/fuelPriceMapper';
import './AnalyticsDashboard.css';

const FuelKpiCardSkeleton = () => (
  <div
    className="styleCard"
    style={{
      width: '100%',
      height: '190px',
      boxSizing: 'border-box',
    }}
  >
    <div className="styleCardContent">
      <Skeleton variant="text" width="55%" height={24} />
      <div className="styleDescription">
        <div className="styleCardBody">
          <Skeleton variant="text" width={120} height={56} />
          <Skeleton variant="text" width="65%" height={20} />
        </div>
      </div>
    </div>
  </div>
);

const FuelTableSkeleton = ({ columnCount = 8 }) => (
  <div className="fuel-analytics-table-section">
    <div className="fuel-analytics-table-header">
      <Skeleton variant="text" width="40%" height={28} />
    </div>
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: '1rem',
          padding: '0.75rem',
          background: '#F9FAFB',
        }}
      >
        {[...Array(columnCount).keys()].map((index) => (
          <Skeleton key={`fuel-table-skeleton-th-${index}`} variant="text" width="100%" height={20} />
        ))}
      </div>
      {[...Array(5).keys()].map((rowIndex) => (
        <div
          key={`fuel-table-skeleton-row-${rowIndex}`}
          style={{
            display: 'flex',
            gap: '1rem',
            padding: '0.5rem 0.75rem',
          }}
        >
          {[...Array(columnCount).keys()].map((colIndex) => (
            <Skeleton
              key={`fuel-table-skeleton-cell-${rowIndex}-${colIndex}`}
              variant="text"
              width="100%"
              height={18}
            />
          ))}
        </div>
      ))}
    </div>
  </div>
);

const FuelChartCardSkeleton = ({ height = 320, titleWidth = '45%' }) => (
  <div className="fuel-analytics-table-container">
    <div className="fuel-analytics-chart-container">
      <Skeleton variant="text" width={titleWidth} height={28} />
      <Skeleton variant="text" width="35%" height={20} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" height={height} sx={{ borderRadius: '8px' }} />
    </div>
  </div>
);

/** Parse "H:MM:SS" / "HH:MM:SS" (hours may exceed 99) for sorting. */
const durationStringToSeconds = (value) => {
  if (typeof value !== 'string' || !value.includes(':')) return 0;
  const parts = value.split(':').map((p) => Number(String(p).trim()));
  if (parts.length < 3 || parts.some((n) => !Number.isFinite(n))) return 0;
  const [h, m, s] = parts;
  return h * 3600 + m * 60 + s;
};

const compareFuelTableRows = (a, b, column, order) => {
  const dir = order === 'asc' ? 1 : -1;
  switch (column) {
    case 'totalFuelConsumption':
    case 'idlingRatio':
    case 'fuelEfficiency':
      return dir * (a[column] - b[column]);
    case 'tripFuelCostRM':
    case 'idleWasteCostRM':
    case 'energyConsumption':
    case 'energyCostRM':
    case 'energyEfficiency':
      return dir * ((Number(a[column]) || 0) - (Number(b[column]) || 0));
    case 'durationOn':
    case 'idlingTime':
      return dir * (durationStringToSeconds(a[column]) - durationStringToSeconds(b[column]));
    default:
      return 0;
  }
};

const fuelTableAriaSort = (columnKey, activeColumn, order) => {
  if (activeColumn !== columnKey) return 'none';
  return order === 'asc' ? 'ascending' : 'descending';
};

const fuelTableSortArrowChar = (columnKey, activeColumn, order) => {
  if (activeColumn !== columnKey) return '↕';
  return order === 'desc' ? '↓' : '↑';
};

const fuelTableSortArrowClass = (columnKey, activeColumn) => (
  activeColumn === columnKey
    ? 'fuel-table-sort-arrow fuel-table-sort-arrow-active'
    : 'fuel-table-sort-arrow fuel-table-sort-arrow-idle'
);

/** Format dashboard metrics with thousands separators (e.g. 20424.66 → 20,424.66). */
const formatMetricNumber = (value, fractionDigits = 2) => {
  const numeric = Number(value);
  const safe = Number.isFinite(numeric) ? numeric : 0;
  return safe.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
};

/**
 * KPI tile change indicator (vs. the previous period).
 *
 * getFuelManagement returns the diff as an ABSOLUTE difference (current − previous) in each
 * metric's native unit, not a percentage (see horizon-insights buildFuelManagementResponse /
 * `difference()`), and both the per-asset mapper and the fleet aggregation keep it absolute.
 * The KPI card, however, renders its `different` prop with a hardcoded `%` suffix — so the raw
 * absolute delta was being mislabelled as a percentage. Here we derive the real percentage change
 * from the tile's own current-period `value` and absolute `change`: since the aggregation is
 * linear, `previous = value − change` for both totals and averages, so
 * `percent = change / previous * 100`.
 *
 * When there is no previous-period baseline (previous ≈ 0) we mirror the backend
 * `reportPercentageChange` convention (getFleetFuelReport): report +100% for any positive current,
 * else 0% — never Infinity/NaN. Returns props consumed directly by <Card>; a zero change keeps the
 * original behaviour (green up-arrow + "0.00%") rather than a neutral state.
 */
const toKpiPercentChangeProps = (kpi) => {
  const value = Number(kpi?.value);
  const change = Number(kpi?.change);
  const safeValue = Number.isFinite(value) ? value : 0;
  const safeChange = Number.isFinite(change) ? change : 0;
  const previous = safeValue - safeChange;

  let percent;
  if (!Number.isFinite(previous) || Math.abs(previous) < 1e-9) {
    percent = safeChange ? 100 : 0;
  } else {
    percent = (safeChange / previous) * 100;
  }

  return {
    different: percent.toFixed(2),
    checkArrow: percent >= 0,
  };
};

/** Resolve idle-waste spec completeness from API row (snake_case or camelCase). */
const resolveIdleWasteSpecComplete = (item) => {
  if (typeof item.idle_waste_spec_complete === 'boolean') return item.idle_waste_spec_complete;
  if (typeof item.idleWasteSpecComplete === 'boolean') return item.idleWasteSpecComplete;
  return undefined;
};

const AnalyticsDashboard = () => {
  const { state } = useLocation();
  const token = Cookies.get('jwtToken');
  // const agencyID = Cookies.get('agency-id');
  const agencyID = Cookies.get('agencyUUID');
  const agencyIdSlug = Cookies.get('agency-id');
  const persistedAgencyTimezone = Cookies.get(AGENCY_TIMEZONE_COOKIE);
  const agencyTimezone = resolveAgencyTimezone({
    privileges: localStorage.getItem('listAgency'),
    agencyUuid: agencyID,
    agencyId: agencyIdSlug,
    persistedTimezone: persistedAgencyTimezone,
  });
  const [fuelResponseTimezone, setFuelResponseTimezone] = useState(null);
  const authoritativeResponseTimezone = fuelResponseTimezone?.agencyID === agencyID
    ? fuelResponseTimezone.timezone
    : null;
  const effectiveAgencyTimezone = authoritativeResponseTimezone || agencyTimezone;
  // Requests without this value remain offsetless for API-side agency resolution.
  // UTC is only a defensive renderer fallback; it is never used to build boundaries.
  const displayTimezone = effectiveAgencyTimezone || 'UTC';
  const { apiResponse } = useApiData();
  const [loadAsset, setLoadAsset] = useState(true);
  const [navigationClick, setNavigationClick, , setNavigationState] = useOutletContext();
  const [listAsset, setListAsset] = useState([]);
  const [assetSelectionList, setAssetSelectionList] = useState({});
  const [inputSelectonText, setInputSelectonText] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const uuid = state?.uuid || '';
  const [date, setDate] = useState([]);
  const [inputValue, setInputValue] = useState('Select Dates');
  const searchTimeoutRef = useRef(null);
  const [dropDownLoading, setDropDownLoading] = useState(false);
  const [minDateRange, setMinDateRange] = useState(new DateObject().subtract(1, 'year'));
  const [maxDateRange, setMaxDateRange] = useState(new Date());
  const [dateRange, setDateRange] = useState('');
  const hasInitializedSearchEffect = useRef(false);
  const lastFuelRequestKey = useRef('');
  const lastFuelSignalRequestKey = useRef('');

  const [loadFuel, setLoadFuel] = useState(true);
  const [loadFuelSignal, setLoadFuelSignal] = useState(false);
  const [fuelFetchError, setFuelFetchError] = useState('');
  const [fuelSignalError, setFuelSignalError] = useState('');
  const [fuelSignalView, setFuelSignalView] = useState(null);
  const [fuelSignalEmptyReason, setFuelSignalEmptyReason] = useState('');
  const [fleetTimelineView, setFleetTimelineView] = useState(null);
  const [loadFleetTimeline, setLoadFleetTimeline] = useState(false);
  const [fleetTimelineError, setFleetTimelineError] = useState('');
  const [priceRegistryError, setPriceRegistryError] = useState('');
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const [tableSortColumn, setTableSortColumn] = useState('totalFuelConsumption');
  const [tableSortOrder, setTableSortOrder] = useState('desc');

  // Active fleet filter — drives Liters <-> kWh column swapping and which
  // assets appear under the fuel vs energy subtotals.
  const [fleetFilter, setFleetFilter] = useState(FLEET_FILTER.MIXED);
  const [tariffRateByCode, setTariffRateByCode] = useState({});
  const [fuelPriceByCode, setFuelPriceByCode] = useState({});
  const [fuelPriceCurrentRows, setFuelPriceCurrentRows] = useState([]);
  const [fuelPriceHistoryRows, setFuelPriceHistoryRows] = useState([]);
  const [fuelEfficiencyZoomRange, setFuelEfficiencyZoomRange] = useState({ min: null, max: null });
  const fuelEfficiencyChartRef = useRef(null);

  // // Mock data - Hardcoded (commented out, now using API)
  // const mockFuelData = {
  //   kpis: {
  //     mileage: { value: 5068, change: 244.11, isPositive: true },
  //     totalFuelConsumption: { value: 2880, change: 383, isPositive: false },
  //     totalFuelIdling: { value: 1181, change: 252, isPositive: false },
  //     avgFuelConsumption: { value: 6, change: 0.05, isPositive: false },
  //     avgFuelEfficiency: { value: 1.76, change: -0.17, isPositive: false },
  //   },
  //   tableData: [
  //     {
  //       asset: 'WB863U', totalFuelConsumption: 720, fuelIdling: 450,
  //       fuelMoving: 270, durationOn: '180:30:15', idlingTime: '150:20:10',
  //       idlingRatio: 83.25, fuelEfficiency: 1.50,
  //     },
  //     {
  //       asset: 'WB759L', totalFuelConsumption: 650, fuelIdling: 449,
  //       fuelMoving: 201, durationOn: '158:16:10', idlingTime: '139:29:40',
  //       idlingRatio: 88.14, fuelEfficiency: 1.00,
  //     },
  //     {
  //       asset: 'WB123A', totalFuelConsumption: 580, fuelIdling: 380,
  //       fuelMoving: 200, durationOn: '145:20:00', idlingTime: '120:15:30',
  //       idlingRatio: 82.80, fuelEfficiency: 1.65,
  //     },
  //     {
  //       asset: 'WB456B', totalFuelConsumption: 520, fuelIdling: 320,
  //       fuelMoving: 200, durationOn: '130:10:20', idlingTime: '100:05:15',
  //       idlingRatio: 76.90, fuelEfficiency: 1.85,
  //     },
  //     {
  //       asset: 'WB789C', totalFuelConsumption: 410, fuelIdling: 250,
  //       fuelMoving: 160, durationOn: '102:30:45', idlingTime: '75:20:10',
  //       idlingRatio: 73.50, fuelEfficiency: 2.10,
  //     },
  //   ],
  //   fuelEfficiencyChart: [],
  //   fuelConsumptionChart: [],
  // };

  const defaultKpis = {
    mileage: { value: 0, change: 0, isPositive: true },
    totalFuelConsumption: { value: 0, change: 0, isPositive: true },
    totalFuelIdling: { value: 0, change: 0, isPositive: true },
    avgFuelConsumption: { value: 0, change: 0, isPositive: true },
    avgFuelEfficiency: { value: 0, change: 0, isPositive: true },
    totalEnergyConsumption: {
      value: 0, change: 0, isPositive: true, hasValue: false,
    },
    avgEnergyEfficiency: {
      value: 0, change: 0, isPositive: true, hasValue: false,
    },
  };

  const [fuelData, setFuelData] = useState({
    kpis: defaultKpis,
    tableData: [],
    fuelEfficiencyChart: [],
    fuelConsumptionChart: [],
    perAssetFuelConsumption: [],
    perAssetFuelEfficiency: [],
    mileageChart: [],
    energyChart: [],
    assetCapabilities: [],
  });

  const formatPickerDate = (value) => moment(new Date(value)).format('MMM D, YYYY');

  const assetPerformanceNavigationList = [
    {
      text: 'Executive Summary and Fleet Health ', value: 1, path: '/analytics/fleetDashboard', enabled: (apiResponse?.role === 'Agency Administrator' || apiResponse?.role === 'Agency Owner') ? true : apiResponse?.module?.analytics?.fleetDashboard,
    },
    {
      text: 'Fuel Management System', value: 2, path: '/analytics/analyticsDashboard', enabled: (apiResponse?.role === 'Agency Administrator' || apiResponse?.role === 'Agency Owner') ? true : apiResponse?.module?.analytics?.analyticsDashboard,
    },
    {
      text: 'VCM & Maintenance Management', value: 3, path: '/analytics/batteryDashboard', enabled: (apiResponse?.role === 'Agency Administrator' || apiResponse?.role === 'Agency Owner') ? true : apiResponse?.module?.analytics?.batteryDashboard,
    },
  ];

  // // Mock chart data (commented out, now using API)
  // const generateMockChartData = () => {
  //   const fuelEfficiencyData = [];
  //   const fuelConsumptionData = [];
  //   const mileageData = [];
  //   const startDate = moment().subtract(6, 'days').startOf('day');
  //   for (let i = 0; i < 7; i += 1) {
  //     const chartDate = moment(startDate).add(i, 'days');
  //     fuelEfficiencyData.push({ x: chartDate.valueOf(), y: (Math.random() * 0.5 + 1.5).toFixed(2) });
  //     fuelConsumptionData.push({ x: chartDate.valueOf(), moving: Math.random() * 10 + 15, idling: Math.random() * 5 + 5 });
  //     mileageData.push({ x: chartDate.valueOf(), y: Math.random() * 30 + 50 });
  //   }
  //   return { fuelEfficiencyData, fuelConsumptionData, mileageData };
  // };

  const toNumber = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  };

  // Return the first finite numeric candidate, or undefined when none qualify.
  // Used for optional backend cost fields so "missing" stays distinct from 0.
  const pickNumeric = (...candidates) => {
    for (let i = 0; i < candidates.length; i += 1) {
      const candidate = candidates[i];
      if (candidate !== undefined && candidate !== null && candidate !== '') {
        const numeric = Number(candidate);
        if (Number.isFinite(numeric)) return numeric;
      }
    }
    return undefined;
  };

  const pickBoolean = (...candidates) => candidates.find(
    (candidate) => typeof candidate === 'boolean',
  );

  const toTimestamp = (value) => {
    const timestamp = moment(value).valueOf();
    return Number.isFinite(timestamp) ? timestamp : null;
  };

  const normalizeFuelEfficiencyPoint = (value) => {
    const numericValue = toNumber(value);
    // Guard against backend outliers/invalid values (e.g. malformed big integers).
    if (numericValue < 0 || numericValue > 100) return null;
    return numericValue;
  };

  const formatSecondsToDuration = (value) => {
    const seconds = Number(value);
    if (!Number.isFinite(seconds)) return '00:00:00';
    const safeSeconds = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const remainingSeconds = safeSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDurationValue = (value) => {
    if (typeof value === 'string' && value.includes(':')) return value;
    return formatSecondsToDuration(value);
  };

  const mapFuelApiPayload = (payload) => {
    const [res = {}] = payload?.data || [];
    const mileage = res?.mileage?.[0] || {};
    const usage = res?.usage?.[0] || {};
    const idleUsage = res?.idle_usage?.[0] || {};
    const fuelRate = res?.fuel_rate?.[0] || {};
    const fuelEfficiency = res?.fuel_efficiency?.[0] || {};
    const energy = res?.energy?.[0] || {};
    const energyEfficiency = res?.energy_efficiency?.[0] || {};

    const totalEnergyKwh = pickNumeric(
      energy.current_period_total_kwh,
      energy.currentPeriodTotalKwh,
      energy.current_period_usage,
      energy.currentPeriodUsage,
      res.total_energy_kwh,
      res.totalEnergyKwh,
    );
    const energyKwhChange = pickNumeric(
      energy.difference_in_total_kwh,
      energy.differenceInTotalKwh,
    );
    const avgEnergyEff = pickNumeric(
      energyEfficiency.current_period_avg_energy_efficiency,
      energyEfficiency.currentPeriodAvgEnergyEfficiency,
    );
    const avgEnergyEffChange = pickNumeric(
      energyEfficiency.difference_in_energy_efficiency,
      energyEfficiency.differenceInEnergyEfficiency,
    );

    return {
      kpis: {
        mileage: {
          value: toNumber(mileage.current_period_mileage),
          change: toNumber(mileage.difference_in_mileage),
        },
        totalFuelConsumption: {
          value: toNumber(usage.current_period_usage),
          change: toNumber(usage.difference_in_usage),
        },
        totalFuelIdling: {
          value: toNumber(idleUsage.current_period_idle_usage),
          change: toNumber(idleUsage.difference_in_idle_usage),
        },
        avgFuelConsumption: {
          value: toNumber(fuelRate.current_period_avg_fuel_rate),
          change: toNumber(fuelRate.difference_in_fuel_rate),
        },
        avgFuelEfficiency: {
          value: toNumber(fuelEfficiency.current_period_avg_fuel_efficiency),
          change: toNumber(fuelEfficiency.difference_in_fuel_efficiency),
        },
        totalEnergyConsumption: {
          value: totalEnergyKwh ?? 0,
          change: energyKwhChange ?? 0,
          isPositive: (energyKwhChange ?? 0) >= 0,
          hasValue: totalEnergyKwh !== undefined,
        },
        avgEnergyEfficiency: {
          value: avgEnergyEff ?? 0,
          change: avgEnergyEffChange ?? 0,
          isPositive: (avgEnergyEffChange ?? 0) >= 0,
          hasValue: avgEnergyEff !== undefined,
        },
      },
      // API payloads for analytics can vary by service/version:
      // some fields come in snake_case, others in camelCase.
      // We intentionally normalize both formats here to keep UI stable.
      tableData: (res.fuel_efficiency_breakdown || []).map((item) => ({
        asset: item.asset_name || item.assetName || '',
        totalFuelConsumption: toNumber(
          item.total_fuel_consumption
          || item.totalFuelConsumption
          || item.usage_per_asset
          || item.usagePerAsset,
        ),
        fuelIdling: toNumber(
          item.fuel_consumption_idling
          || item.fuelIdling
          || item.idle_usage_per_asset
          || item.idleUsagePerAsset
          || item.idle_waste_fuel_per_asset
          || item.idleWasteFuelPerAsset,
        ),
        fuelMoving: pickNumeric(
          item.fuel_consumption_moving,
          item.fuelMoving,
          item.moving_usage_per_asset,
          item.movingUsagePerAsset,
        ),
        durationOn: formatDurationValue(
          item.duration_on
          || item.durationOn
          || item.duration_on_per_asset
          || item.durationOnPerAsset,
        ),
        idlingTime: formatDurationValue(
          item.idling_time
          || item.idlingTime
          || item.duration_idle_per_asset
          || item.durationIdlePerAsset,
        ),
        idlingRatio: toNumber(
          (() => {
            const rawRatio = toNumber(
              item.idling_ratio
              || item.idlingRatio
              || item.idle_ratio_per_asset
              || item.idleRatioPerAsset,
            );
            return rawRatio <= 1 ? rawRatio * 100 : rawRatio;
          })(),
        ),
        fuelEfficiency: toNumber(
          item.fuel_efficiency
          || item.fuelEfficiency
          || item.fuel_efficiency_per_asset
          || item.fuelEfficiencyPerAsset,
        ),
        // Authoritative backend fields (GET /v3/getFuelManagement handoff).
        apiTripFuelCostRM: pickNumeric(item.trip_fuel_cost_rm, item.tripFuelCostRm),
        apiIdleWasteFuel: pickNumeric(
          item.idle_waste_fuel_per_asset,
          item.idleWasteFuelPerAsset,
        ),
        apiIdleWasteCostRM: pickNumeric(item.idle_waste_cost_rm, item.idleWasteCostRm),
        apiEnergyCostRM: pickNumeric(item.total_energy_cost_rm, item.totalEnergyCostRm),
        apiEnergyConsumption: pickNumeric(
          item.total_energy_kwh,
          item.totalEnergyKwh,
          item.energy_usage_per_asset,
          item.energyUsagePerAsset,
          item.total_energy_consumption,
          item.totalEnergyConsumptionKwh,
          item.usage_per_asset,
          item.usagePerAsset,
        ),
        apiEnergyUsage: pickNumeric(
          item.energy_usage_per_asset,
          item.energyUsagePerAsset,
        ),
        apiEnergyEfficiency: pickNumeric(
          item.energy_efficiency,
          item.energyEfficiency,
          item.energy_efficiency_per_asset,
          item.energyEfficiencyPerAsset,
        ),
        apiMileageKm: pickNumeric(
          item.mileage_per_asset,
          item.mileagePerAsset,
          item.total_mileage_per_asset,
          item.totalMileagePerAsset,
          item.asset_mileage,
          item.assetMileage,
          item.total_distance_km,
          item.totalDistanceKm,
        ),
        apiFuelPricePerLitre: pickNumeric(item.fuel_price_per_litre, item.fuelPricePerLitre),
        apiEnergyPricePerKwh: pickNumeric(item.energy_price_per_kwh, item.energyPricePerKwh),
        apiFuelType: item.fuel_type ?? item.fuelType ?? null,
        apiChargingTariff: resolveApiChargingTariff(item),
        apiIdleWasteSpecComplete: resolveIdleWasteSpecComplete(item),
        apiAssetType: item.asset_type ?? item.assetType ?? null,
        apiFleetClass: item.fleet_class ?? item.fleetClass ?? null,
        apiSupportsFuel: pickBoolean(item.supports_fuel, item.supportsFuel),
        apiSupportsEnergy: pickBoolean(item.supports_energy, item.supportsEnergy),
      })),
      assetCapabilities: (res.asset_capabilities || res.assetCapabilities || []).map((item) => ({
        asset: item.asset_name || item.assetName || '',
        assetType: item.asset_type ?? item.assetType ?? null,
        fleetClass: item.fleet_class ?? item.fleetClass ?? null,
        supportsFuel: pickBoolean(item.supports_fuel, item.supportsFuel),
        supportsEnergy: pickBoolean(item.supports_energy, item.supportsEnergy),
      })),
      fuelEfficiencyChart: (res.fuel_efficiency_with_time || [])
        .map((p) => ({
          x: toTimestamp(p?.bqTimestampHour?.value),
          y: normalizeFuelEfficiencyPoint(p?.fuel_efficiency_per_hour),
        }))
        .filter((p) => p.x !== null && p.y !== null),
      fuelConsumptionChart: (res.fuel_consumption_mileage_with_time || [])
        .map((p) => ({
          x: toTimestamp(p?.bqTimestampHour?.value),
          // moving: toNumber(p?.fuel_consumption_moving_per_hour),
          // idling: toNumber(p?.fuel_consumption_idle_per_hour),
          fuelConsumption: toNumber(p?.fuel_consumption_per_hour),
        }))
        .filter((p) => p.x !== null),
      mileageChart: (res.fuel_consumption_mileage_with_time || [])
        .map((p) => ({
          x: toTimestamp(p?.bqTimestampHour?.value),
          y: toNumber(p?.total_mileage_per_hour),
        }))
        .filter((p) => p.x !== null),
      energyChart: (res.energy_with_time || [])
        .map((p) => ({
          x: toTimestamp(p?.bqTimestampHour?.value),
          y: pickNumeric(p?.energy_kwh_per_hour, p?.energyKwhPerHour),
        }))
        .filter((p) => p.x !== null && p.y !== undefined),
    };
  };

  const average = (values) => {
    if (values.length === 0) return 0;
    return values.reduce((sum, x) => sum + x, 0) / values.length;
  };

  const aggregateByTimestamp = (items, valueKey, mode = 'sum') => {
    const bucket = new Map();
    items.forEach((item) => {
      const current = bucket.get(item.x) || { total: 0, count: 0 };
      bucket.set(item.x, {
        total: current.total + toNumber(item[valueKey]),
        count: current.count + 1,
      });
    });
    return Array.from(bucket.entries())
      .map(([x, agg]) => ({
        x: Number(x),
        y: mode === 'avg' ? (agg.total / agg.count) : agg.total,
      }))
      .sort((a, b) => a.x - b.x);
  };

  const noopSetLoading = () => {};

  const parseFuelPayloadsFromResult = (result) => {
    if (result?.data?.status !== 200) return [];
    const responseData = result?.data?.data;
    if (Array.isArray(responseData)) return responseData;
    if (responseData && typeof responseData === 'object') return [responseData];
    return [];
  };

  const resolvePerAssetFuelChartSeries = (
    successfulPayloads,
    rawPayloads,
  ) => {
    if (successfulPayloads.length > 1) {
      return {
        consumption: buildPerAssetFuelSeriesFromPayloads(successfulPayloads),
        efficiency: buildPerAssetFuelEfficiencySeriesFromPayloads(successfulPayloads),
      };
    }

    if (rawPayloads.length === 1) {
      const groupedConsumption = extractPerAssetFuelSeriesFromResponse(rawPayloads[0]);
      const groupedEfficiency = extractPerAssetFuelEfficiencySeriesFromResponse(rawPayloads[0]);
      if (groupedConsumption?.length > 0 || groupedEfficiency?.length > 0) {
        return {
          consumption: groupedConsumption || buildPerAssetFuelSeriesFromPayloads(successfulPayloads),
          efficiency: groupedEfficiency || buildPerAssetFuelEfficiencySeriesFromPayloads(successfulPayloads),
        };
      }
    }

    // Never fan out one report request per vehicle. A bulk response from the patched
    // backend includes asset_name on every hourly point; older responses simply omit
    // the optional per-asset charts while keeping the fleet summary responsive.
    if ((successfulPayloads[0]?.tableData?.length || 0) > 1) {
      return { consumption: [], efficiency: [] };
    }

    return {
      consumption: buildPerAssetFuelSeriesFromPayloads(successfulPayloads),
      efficiency: buildPerAssetFuelEfficiencySeriesFromPayloads(successfulPayloads),
    };
  };

  const fetchFuelData = async (fromTime, toTime, assets = listAsset) => {
    if (!token || !agencyID) return;
    const requestAssetKey = assets.map((asset) => asset?.id).filter(Boolean).join('|');
    const currentRequestKey = `${fromTime}|${toTime}|${requestAssetKey}`;
    const isCurrentRequest = () => lastFuelRequestKey.current === currentRequestKey;

    setLoadFuel(true);
    setFuelFetchError('');
    try {
      const assetNamesParam = assets.map((asset) => asset?.id).filter((name) => Boolean(name));

      if (assetNamesParam.length === 0) {
        if (!isCurrentRequest()) return;
        setFuelData({
          kpis: defaultKpis,
          tableData: [],
          fuelEfficiencyChart: [],
          fuelConsumptionChart: [],
          perAssetFuelConsumption: [],
          perAssetFuelEfficiency: [],
          mileageChart: [],
          energyChart: [],
          assetCapabilities: [],
        });
        setLoadFuel(false);
        return;
      }

      // Single bulk request (same model as Fleet Health).
      const result = await APIGetV3FuelManagement(
        setLoadFuel,
        token,
        agencyID,
        fromTime,
        toTime,
        assetNamesParam,
      );

      if (!isCurrentRequest()) return;

      const successfulPayloads = [];
      const rawPayloads = [];
      if (result?.data?.status === 200) {
        const normalizedPayloads = parseFuelPayloadsFromResult(result);
        const responseTimezone = resolveFuelResponseTimezone(normalizedPayloads);
        if (responseTimezone) {
          setFuelResponseTimezone({ agencyID, timezone: responseTimezone });
        }
        normalizedPayloads.forEach((payload) => {
          rawPayloads.push(payload);
          successfulPayloads.push(mapFuelApiPayload({ data: [payload] }));
        });
      }

      if (successfulPayloads.length === 0) {
        if (!isCurrentRequest()) return;
        setFuelData({
          kpis: defaultKpis,
          tableData: [],
          fuelEfficiencyChart: [],
          fuelConsumptionChart: [],
          perAssetFuelConsumption: [],
          perAssetFuelEfficiency: [],
          mileageChart: [],
          energyChart: [],
          assetCapabilities: [],
        });
        setFuelFetchError('Unable to load fuel analysis data. Please try again.');
        setLoadFuel(false);
        return;
      }

      const fuelEffPoints = [];
      const fuelConsumptionPoints = [];
      const mileagePoints = [];
      const energyPoints = [];

      successfulPayloads.forEach((payload) => {
        fuelEffPoints.push(...payload.fuelEfficiencyChart);
        fuelConsumptionPoints.push(...payload.fuelConsumptionChart.map((p) => ({ x: p.x, y: p.fuelConsumption })));
        mileagePoints.push(...payload.mileageChart);
        energyPoints.push(...payload.energyChart);
      });

      const tableData = successfulPayloads
        .flatMap((payload) => payload.tableData)
        .filter((row) => row.asset);
      const assetCapabilities = successfulPayloads
        .flatMap((payload) => payload.assetCapabilities || [])
        .filter((capability) => capability.asset);

      const kpiMileage = successfulPayloads.map((p) => p.kpis.mileage);
      const kpiUsage = successfulPayloads.map((p) => p.kpis.totalFuelConsumption);
      const kpiIdle = successfulPayloads.map((p) => p.kpis.totalFuelIdling);
      const kpiAvgRate = successfulPayloads.map((p) => p.kpis.avgFuelConsumption);
      const kpiAvgEff = successfulPayloads.map((p) => p.kpis.avgFuelEfficiency);
      const kpiEnergy = successfulPayloads.map((p) => p.kpis.totalEnergyConsumption);
      const kpiAvgEnergyEff = successfulPayloads.map((p) => p.kpis.avgEnergyEfficiency);

      const aggregatedFuelEfficiency = aggregateByTimestamp(fuelEffPoints, 'y', 'avg');
      const aggregatedFuelConsumption = aggregateByTimestamp(fuelConsumptionPoints, 'y', 'sum');
      const aggregatedMileage = aggregateByTimestamp(mileagePoints, 'y', 'sum');
      const aggregatedEnergy = aggregateByTimestamp(energyPoints, 'y', 'sum');

      const fuelConsumptionChart = aggregatedFuelConsumption.map((point) => ({
        x: point.x,
        fuelConsumption: point.y,
        // moving: point.y,
        // idling: aggregatedFuelIdling.find((p) => p.x === point.x)?.y || 0,
      }));

      const { consumption: perAssetFuelConsumption, efficiency: perAssetFuelEfficiency } = (
        resolvePerAssetFuelChartSeries(successfulPayloads, rawPayloads)
      );

      if (!isCurrentRequest()) return;

      setFuelData({
        kpis: {
          mileage: {
            value: kpiMileage.reduce((sum, x) => sum + x.value, 0),
            change: kpiMileage.reduce((sum, x) => sum + x.change, 0),
            isPositive: kpiMileage.reduce((sum, x) => sum + x.change, 0) >= 0,
          },
          totalFuelConsumption: {
            value: kpiUsage.reduce((sum, x) => sum + x.value, 0),
            change: kpiUsage.reduce((sum, x) => sum + x.change, 0),
            isPositive: kpiUsage.reduce((sum, x) => sum + x.change, 0) >= 0,
          },
          totalFuelIdling: {
            value: kpiIdle.reduce((sum, x) => sum + x.value, 0),
            change: kpiIdle.reduce((sum, x) => sum + x.change, 0),
            isPositive: kpiIdle.reduce((sum, x) => sum + x.change, 0) >= 0,
          },
          avgFuelConsumption: {
            value: average(kpiAvgRate.map((x) => x.value)),
            change: average(kpiAvgRate.map((x) => x.change)),
            isPositive: average(kpiAvgRate.map((x) => x.change)) >= 0,
          },
          avgFuelEfficiency: {
            value: average(kpiAvgEff.map((x) => x.value)),
            change: average(kpiAvgEff.map((x) => x.change)),
            isPositive: average(kpiAvgEff.map((x) => x.change)) >= 0,
          },
          totalEnergyConsumption: {
            value: kpiEnergy.reduce((sum, x) => sum + x.value, 0),
            change: kpiEnergy.reduce((sum, x) => sum + x.change, 0),
            isPositive: kpiEnergy.reduce((sum, x) => sum + x.change, 0) >= 0,
            hasValue: kpiEnergy.some((x) => x.hasValue),
          },
          avgEnergyEfficiency: {
            value: average(kpiAvgEnergyEff.filter((x) => x.hasValue).map((x) => x.value)),
            change: average(kpiAvgEnergyEff.filter((x) => x.hasValue).map((x) => x.change)),
            isPositive: average(kpiAvgEnergyEff.filter((x) => x.hasValue).map((x) => x.change)) >= 0,
            hasValue: kpiAvgEnergyEff.some((x) => x.hasValue),
          },
        },
        tableData,
        fuelEfficiencyChart: aggregatedFuelEfficiency,
        fuelConsumptionChart,
        perAssetFuelConsumption,
        perAssetFuelEfficiency,
        mileageChart: aggregatedMileage,
        energyChart: aggregatedEnergy,
        assetCapabilities,
      });
      setLoadFuel(false);
    } catch (err) {
      if (!isCurrentRequest()) return;
      setFuelFetchError('Unable to load fuel analysis data. Please try again.');
      setLoadFuel(false);
    }
  };

  const selectedAssetName = assetSelectionList?.id || null;
  const selectedDeviceOid = assetSelectionList?.uuid || null;
  const selectedFuelSource = normalizeTelemetrySourceKey(assetSelectionList?.fuelSource);
  const selectedApiFuelSource = normalizeApiFuelSourceKey(assetSelectionList?.fuelSource);
  const selectedTankCapacityL = toNumber(assetSelectionList?.fuelCapacity) || 0;

  const fetchCachedFuelLevelSignal = async (fromTime, toTime, deviceOid, apiFuelSource) => {
    const cacheKey = buildFuelLevelSignalCacheKey(deviceOid, fromTime, toTime, apiFuelSource);
    const cached = readFuelLevelSignalCache(cacheKey);
    if (cached) return cached;

    const result = await APIGetV3FuelLevelSignal(
      noopSetLoading,
      token,
      agencyID,
      fromTime,
      toTime,
      deviceOid,
      apiFuelSource,
    );
    const httpStatus = result?.status ?? result?.response?.status ?? null;
    if (httpStatus == null || httpStatus < 400) {
      writeFuelLevelSignalCache(cacheKey, result);
    }
    return result;
  };

  const requestAssets = useMemo(() => {
    if (selectedAssetName && assetSelectionList?.uuid) {
      return [assetSelectionList];
    }
    return listAsset;
  }, [selectedAssetName, assetSelectionList, listAsset]);

  const requestAssetsKey = useMemo(
    () => requestAssets.map((asset) => asset?.id).filter(Boolean).join('|'),
    [requestAssets],
  );

  const fuelSignalFilterRange = useMemo(() => {
    if (date.length < 2) return { start: null, end: null };
    const requestRange = buildAgencyUtcDayRange(date[0], date[1], effectiveAgencyTimezone);
    if (!requestRange) return { start: null, end: null };
    return {
      start: Date.parse(requestRange.fromTime),
      end: Date.parse(requestRange.toTime) - 1,
    };
  }, [date, effectiveAgencyTimezone]);

  const filterDayCount = useMemo(() => {
    if (fuelSignalFilterRange.start == null || fuelSignalFilterRange.end == null) return 0;
    const start = moment(fuelSignalFilterRange.start).tz(displayTimezone).startOf('day');
    const end = moment(fuelSignalFilterRange.end).tz(displayTimezone).startOf('day');
    return end.diff(start, 'days') + 1;
  }, [fuelSignalFilterRange, displayTimezone]);

  const formatDailyChartRangeDate = (value) => {
    if (value == null) return '';
    return moment(value).tz(displayTimezone).format('MMM DD, YYYY');
  };

  const isFuelEfficiencyZoomed = fuelEfficiencyZoomRange.min != null
    && fuelEfficiencyZoomRange.max != null;

  const fetchFuelLevelSignal = async (fromTime, toTime, requestKey) => {
    if (!token || !agencyID || !selectedDeviceOid) {
      setFuelSignalView(null);
      setFuelSignalError('');
      setFuelSignalEmptyReason('');
      return;
    }

    const isCurrentRequest = () => lastFuelSignalRequestKey.current === requestKey;

    setLoadFuelSignal(true);
    setFuelSignalError('');
    setFuelSignalEmptyReason('');
    const buildOptions = {
      fuelSource: selectedFuelSource,
      tankCapacityL: selectedTankCapacityL,
    };

    try {
      // Single request — fuel level signal must come from the dedicated API, not telemetryLogReport.
      const signalResult = await fetchCachedFuelLevelSignal(
        fromTime,
        toTime,
        selectedDeviceOid,
        selectedApiFuelSource,
      );
      if (!isCurrentRequest()) return;

      const {
        rows, apiEvents, sampleCounts, totalData, status, httpStatus, isNoContent,
      } = resolveFuelSignalApiPayload(signalResult);

      if (httpStatus === 404 || httpStatus === 501) {
        setFuelSignalView(null);
        setFuelSignalEmptyReason(
          'Fuel level signal API (GET /v3/getFuelLevelSignal) is not available yet.',
        );
        setLoadFuelSignal(false);
        return;
      }

      if (rows.length === 0) {
        setFuelSignalView(null);
        if (isNoContent || httpStatus === 204 || status === 204) {
          setFuelSignalEmptyReason(
            'No raw fuel samples are available for this asset and date range. Check its fuel source and tank-capacity or mapping configuration.',
          );
        } else if (sampleCounts?.rawInputSamples > 0
          && sampleCounts?.fuelBearingSamples === 0) {
          setFuelSignalEmptyReason(
            `API queried ${sampleCounts.rawInputSamples.toLocaleString()} raw samples, but none contained fuel readings for the configured source.`,
          );
        } else if (sampleCounts?.rawInputSamples > 0
          && sampleCounts?.cleanSamples === 0) {
          setFuelSignalEmptyReason(
            `API queried ${sampleCounts.rawInputSamples.toLocaleString()} raw samples, but no fuel samples remained after filtering.`,
          );
        } else if (totalData > 0) {
          setFuelSignalEmptyReason(
            `API reports ${totalData.toLocaleString()} samples but returned no inline rows. Backend may need pagination or a smaller default window.`,
          );
        } else {
          setFuelSignalEmptyReason(
            'No fuel level signal samples for this asset and date range.',
          );
        }
        setLoadFuelSignal(false);
        return;
      }

      const view = buildFuelLevelSignalView(rows, {
        ...buildOptions,
        apiEvents,
        sampleCounts,
      });
      if (!isCurrentRequest()) return;

      if ((view.confirmedSeries?.length || 0) === 0) {
        setFuelSignalView(null);
        setFuelSignalEmptyReason(
          `${rows.length.toLocaleString()} rows returned but none contained fuel level fields. Check fuel source: ${selectedFuelSource || 'not configured'}.`,
        );
        setLoadFuelSignal(false);
        return;
      }

      setFuelSignalView(view);
      setLoadFuelSignal(false);
    } catch (err) {
      if (!isCurrentRequest()) return;
      setFuelSignalView(null);
      setFuelSignalError('Unable to load fuel level signal data.');
      setFuelSignalEmptyReason('');
      setLoadFuelSignal(false);
    }
  };

  const handleFleetTimelineAssetClick = (assetName) => {
    const asset = listAsset.find((entry) => entry.id === assetName);
    if (!asset) return;
    const assetIndex = listAsset.findIndex((entry) => entry.uuid === asset.uuid);
    setAssetSelectionList(asset);
    if (assetIndex >= 0) {
      setSelectedOption(assetIndex);
    }
    setInputSelectonText(asset.id || '');
  };

  useEffect(() => {
    if (!token || !agencyID || date.length < 2 || requestAssets.length === 0) return;
    const requestRange = buildAgencyRequestDayRange(date[0], date[1], agencyTimezone);
    if (!requestRange) return;
    const { fromTime, toTime } = requestRange;
    const requestKey = `${fromTime}|${toTime}|${requestAssetsKey}`;
    if (lastFuelRequestKey.current === requestKey) return;
    lastFuelRequestKey.current = requestKey;
    fetchFuelData(fromTime, toTime, requestAssets);
  }, [date, requestAssetsKey, requestAssets.length, token, agencyID, agencyTimezone]);

  // If asset search finished with no devices, stop showing the skeleton
  useEffect(() => {
    if (!loadAsset && listAsset.length === 0) {
      setLoadFuel(false);
    }
  }, [loadAsset, listAsset.length]);

  useEffect(() => {
    if (!token || !agencyID || date.length < 2 || !selectedDeviceOid) {
      setFuelSignalView(null);
      setFuelSignalError('');
      setFuelSignalEmptyReason('');
      return;
    }
    const requestRange = buildAgencyRequestDayRange(date[0], date[1], agencyTimezone);
    if (!requestRange) return;
    const { fromTime, toTime } = requestRange;
    const requestKey = `${fromTime}|${toTime}|${selectedDeviceOid}|${selectedApiFuelSource}`;
    if (lastFuelSignalRequestKey.current === requestKey) return;
    lastFuelSignalRequestKey.current = requestKey;
    fetchFuelLevelSignal(fromTime, toTime, requestKey);
  }, [date, selectedDeviceOid, selectedApiFuelSource, selectedTankCapacityL, token, agencyID, agencyTimezone]);

  useEffect(() => {
    if (!token || !agencyID || date.length < 2 || fleetFilter === FLEET_FILTER.EV) {
      setFleetTimelineView(null);
      setFleetTimelineError('');
      setLoadFleetTimeline(false);
      return undefined;
    }

    if (selectedDeviceOid && selectedAssetName) {
      if (loadFuelSignal) {
        setLoadFleetTimeline(true);
        setFleetTimelineError('');
        return undefined;
      }
      if (fuelSignalError) {
        setFleetTimelineView(null);
        setFleetTimelineError(fuelSignalError);
        setLoadFleetTimeline(false);
        return undefined;
      }
      if (fuelSignalView && fuelSignalFilterRange.start != null && fuelSignalFilterRange.end != null) {
        setFleetTimelineView(buildFleetEventTimelineView(
          [{
            asset: selectedAssetName,
            deviceOid: selectedDeviceOid,
            events: fuelSignalView.events || [],
          }],
          [selectedAssetName],
          {
            filterRangeStart: fuelSignalFilterRange.start,
            filterRangeEnd: fuelSignalFilterRange.end,
          },
        ));
        setFleetTimelineError('');
        setLoadFleetTimeline(false);
      } else {
        setFleetTimelineView(null);
        setLoadFleetTimeline(false);
      }
      return undefined;
    }

    // Loading raw classified samples for every fleet vehicle can return millions of
    // points and issue one BigQuery request per asset. Load this detail only after the
    // user selects a vehicle; the fleet summary and per-asset charts remain bulk calls.
    setFleetTimelineView(null);
    setFleetTimelineError('');
    setLoadFleetTimeline(false);
    return undefined;
  }, [
    date,
    fleetFilter,
    fuelSignalError,
    fuelSignalFilterRange.end,
    fuelSignalFilterRange.start,
    fuelSignalView,
    loadFuelSignal,
    requestAssetsKey,
    requestAssets.length,
    selectedAssetName,
    selectedDeviceOid,
    token,
    agencyID,
    agencyTimezone,
  ]);

  const APICalledSearchAssetNGroup = async (inputText = '') => {
    if (!token || !agencyID) {
      setListAsset([]);
      setLoadAsset(false);
      return;
    }
    const params = {
      pageNumber: 1,
      totalPerPage: 100,
      searchKey: inputText,
    };
    try {
      const result = await APIGetDevices(setLoadAsset, token, agencyID, params);
      const isSuccess = result?.data?.status === 200 || result?.status === 200;
      if (isSuccess) {
        const data = result?.data?.data || result?.data || [];
        const asset = data && data.length > 0 ? data.map((x) => {
          const { fuelType, chargingTariff } = resolveAssetFuelTypes(x);
          const presetTankL = toNumber(x.vehiclePreset?.fuelSetup?.maxTankVolumeL);
          return {
            uuid: x.oid,
            id: x.assetName,
            name: x.deviceSerial ? x.deviceSerial : '',
            pairId: x.oid,
            pairedAssets: [],
            fuelSource: x.fuelSource,
            mileageSource: x.mileageSource,
            fuelCapacity: toNumber(x.fuelCapacity) || presetTankL || 0,
            assetType: Array.isArray(x.assetType) ? x.assetType[0] : x.assetType,
            fuelType,
            chargingTariff,
          };
        }) : [];
        const sortCompare = asset.length > 0 ? _.sortBy(asset, 'name') : [];
        setListAsset((previousList) => {
          const prevKeys = previousList.map((item) => item.uuid).join('|');
          const nextKeys = sortCompare.map((item) => item.uuid).join('|');
          return prevKeys === nextKeys ? previousList : sortCompare;
        });
        if (state?.uuid) {
          const selectedAsset = asset.find((e) => e.uuid === uuid);
          if (selectedAsset?.uuid !== assetSelectionList?.uuid) {
            setSelectedOption(asset.findIndex((e) => e.uuid === uuid));
            setAssetSelectionList(selectedAsset);
          }
        }
        setLoadAsset(false);
      } else {
        setListAsset([]);
        setLoadAsset(false);
      }
    } catch (err) {
      setLoadAsset(false);
    }
  };

  useEffect(() => {
    if (!token || !agencyID) {
      setTariffRateByCode({});
      setFuelPriceByCode({});
      setFuelPriceCurrentRows([]);
      setPriceRegistryError('');
      return undefined;
    }
    let cancelled = false;
    setPriceRegistryError('');
    Promise.all([
      APIGetActiveEvTariffs(token, agencyID).catch(() => null),
      APIGetCurrentFuelPrices(token, agencyID).catch(() => null),
    ])
      .then(([tariffResult, fuelPriceResult]) => {
        if (cancelled) return;
        const tariffFailed = !tariffResult;
        const fuelPriceFailed = !fuelPriceResult;
        if (tariffFailed && fuelPriceFailed) {
          setPriceRegistryError('Unable to load fuel and energy prices. Cost columns may show as pending.');
        } else if (tariffFailed || fuelPriceFailed) {
          setPriceRegistryError('Some price registry data is unavailable. Related cost columns may show as pending.');
        } else {
          setPriceRegistryError('');
        }
        if (tariffResult) {
          const tariffRows = getActiveCurrentEvTariffRows(
            normalizeEvTariffRows(extractEvTariffData(tariffResult)),
          );
          setTariffRateByCode(buildTariffRateMap(tariffRows));
        } else {
          setTariffRateByCode({});
        }
        if (fuelPriceResult) {
          const fuelPriceRows = buildFuelPriceRows(extractFuelPriceData(fuelPriceResult));
          setFuelPriceCurrentRows(fuelPriceRows);
          setFuelPriceByCode(buildFuelPriceMap(fuelPriceRows));
        } else {
          setFuelPriceCurrentRows([]);
          setFuelPriceByCode({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, agencyID]);

  // Only the range end feeds the price-history query (see buildFuelPriceHistoryQuery),
  // so key the fetch on that alone — moving the range start must not refetch.
  const fuelPriceRangeEndKey = useMemo(
    () => (date.length < 2 ? null : toCivilDateKey(date[1])),
    [date],
  );

  useEffect(() => {
    if (!token || !agencyID || !fuelPriceRangeEndKey) {
      setFuelPriceHistoryRows([]);
      return undefined;
    }
    let cancelled = false;

    APIGetFuelPriceHistory(token, agencyID, buildFuelPriceHistoryQuery(fuelPriceRangeEndKey))
      .then((result) => {
        if (cancelled) return;
        setFuelPriceHistoryRows(buildFuelPriceRows(extractFuelPriceData(result)));
      })
      .catch(() => {
        if (!cancelled) setFuelPriceHistoryRows([]);
      });

    return () => {
      cancelled = true;
    };
  }, [token, agencyID, fuelPriceRangeEndKey]);

  const fuelPriceRowsForEvents = useMemo(() => {
    const byId = new Map();
    [...fuelPriceHistoryRows, ...fuelPriceCurrentRows].forEach((row) => {
      if (row?.id) byId.set(row.id, row);
    });
    return Array.from(byId.values());
  }, [fuelPriceHistoryRows, fuelPriceCurrentRows]);

  useEffect(() => {
    if (!hasInitializedSearchEffect.current) {
      hasInitializedSearchEffect.current = true;
      return undefined;
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      APICalledSearchAssetNGroup(inputSelectonText);
    }, 500);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [inputSelectonText]);

  useEffect(() => {
    if (token) {
      APICalledSearchAssetNGroup();
    }
    // Default to last 7 days on first load
    const defaultStart = new DateObject().subtract(6, 'days');
    const defaultEnd = new DateObject();
    setDate([defaultStart, defaultEnd]);
    setInputValue(`${formatPickerDate(defaultStart)} - ${formatPickerDate(defaultEnd)}`);
    window.history.replaceState({}, document.title);
    setNavigationClick(2);
    setNavigationState('analytics');
  }, []);

  useEffect(() => {
    if (dropDownLoading) {
      APICalledSearchAssetNGroup('');
      setDropDownLoading(false);
    }
  }, [dropDownLoading]);

  const dailyFuelEfficiencyChart = useMemo(() => {
    const tz = displayTimezone;
    const fuelAssetNames = new Set(
      requestAssets
        .filter((asset) => isFuelClass(classifyFleet(asset.assetType)))
        .map((asset) => asset.id),
    );
    const assetSeries = (fuelData.perAssetFuelEfficiency || []).filter(
      (entry) => fuelAssetNames.has(entry.asset),
    );

    if (assetSeries.length === 0) {
      return {
        series: [], dayCount: 0, yMax: 5, assets: [], chartRangeStart: null, chartRangeEnd: null,
      };
    }

    return buildDailyFuelEfficiencyByAsset(
      assetSeries,
      tz,
      fuelSignalFilterRange.start,
      fuelSignalFilterRange.end,
    );
  }, [
    fuelData.perAssetFuelEfficiency,
    fuelSignalFilterRange.end,
    fuelSignalFilterRange.start,
    requestAssets,
    displayTimezone,
  ]);

  useEffect(() => {
    setFuelEfficiencyZoomRange({ min: null, max: null });
  }, [
    fuelSignalFilterRange.end,
    fuelSignalFilterRange.start,
    fuelData.perAssetFuelEfficiency,
  ]);

  useEffect(() => {
    if (dailyFuelEfficiencyChart.chartRangeStart == null
      || dailyFuelEfficiencyChart.chartRangeEnd == null) return undefined;
    if (dailyFuelEfficiencyChart.series.length === 0) return undefined;
    const timer = window.setTimeout(() => {
      fuelEfficiencyChartRef.current?.chart?.zoomX(
        dailyFuelEfficiencyChart.chartRangeStart,
        dailyFuelEfficiencyChart.chartRangeEnd,
      );
    }, 0);
    return () => window.clearTimeout(timer);
  }, [
    dailyFuelEfficiencyChart.chartRangeEnd,
    dailyFuelEfficiencyChart.chartRangeStart,
    dailyFuelEfficiencyChart.series.length,
  ]);

  const fuelEfficiencyChartOptions = useMemo(() => {
    const assetCount = dailyFuelEfficiencyChart.assets?.length || 0;
    const dataRangeStart = dailyFuelEfficiencyChart.chartRangeStart ?? fuelSignalFilterRange.start;
    const dataRangeEnd = dailyFuelEfficiencyChart.chartRangeEnd ?? fuelSignalFilterRange.end;
    const zoomMax = fuelEfficiencyZoomRange.max ?? dataRangeEnd;
    const efficiencyXMax = dataRangeEnd != null && zoomMax > dataRangeEnd ? dataRangeEnd : zoomMax;
    const strokeWidths = [
      ...Array.from({ length: assetCount }, () => 1.5),
      3.5,
    ];
    const chartColors = [
      ...DAILY_FUEL_ASSET_COLORS.slice(0, assetCount),
      '#00868B',
    ];

    return {
      chart: {
        id: 'daily-fuel-efficiency-chart',
        type: 'line',
        toolbar: { show: false },
        fontFamily: 'Inter',
        animations: { enabled: false },
        zoom: {
          enabled: true,
          type: 'x',
          autoScaleYaxis: true,
        },
        selection: {
          enabled: true,
          type: 'x',
          fill: {
            color: '#00868B',
            opacity: 0.12,
          },
          stroke: {
            width: 1,
            color: '#00868B',
            opacity: 0.5,
            dashArray: 4,
          },
        },
        events: {
          zoomed(_chartContext, { xaxis }) {
            if (xaxis?.min != null && xaxis?.max != null) {
              setFuelEfficiencyZoomRange({ min: xaxis.min, max: xaxis.max });
            }
          },
          scrolled(_chartContext, { xaxis }) {
            if (xaxis?.min != null && xaxis?.max != null) {
              setFuelEfficiencyZoomRange({ min: xaxis.min, max: xaxis.max });
            }
          },
        },
      },
      stroke: {
        curve: 'straight',
        width: strokeWidths,
      },
      markers: {
        size: 0,
        hover: { size: 4 },
      },
      xaxis: {
        type: 'datetime',
        min: fuelEfficiencyZoomRange.min ?? dataRangeStart ?? undefined,
        max: efficiencyXMax ?? undefined,
        labels: {
          formatter: (value) => moment(value).format('MM-DD'),
        },
      },
      yaxis: {
        title: { text: 'Fuel Efficiency (km/L)' },
        min: 0,
        max: dailyFuelEfficiencyChart.yMax || 5,
        labels: {
          formatter: (value) => Number(value).toFixed(1),
        },
      },
      colors: chartColors,
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'left',
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom({ series, dataPointIndex, w }) {
          const dayTs = w.globals.seriesX[0]?.[dataPointIndex];
          const dayLabel = dayTs ? moment(dayTs).format('YYYY-MM-DD') : '';
          const medianIndex = w.globals.seriesNames.length - 1;
          const medianVal = Number(series[medianIndex]?.[dataPointIndex]);
          const medianLine = Number.isFinite(medianVal) && medianVal > 0
            ? `<div class="fuel-daily-tooltip-median">median: ${medianVal.toFixed(2)} km/L</div>`
            : '';
          const rows = w.globals.seriesNames.slice(0, -1).map((name, index) => {
            const val = Number(series[index]?.[dataPointIndex]);
            if (!Number.isFinite(val) || val <= 0) return '';
            const color = w.globals.colors[index];
            return `<div class="fuel-daily-tooltip-row"><span class="fuel-daily-tooltip-dot" style="background:${color}"></span>${name}: ${val.toFixed(2)}</div>`;
          }).filter(Boolean).join('');
          return `<div class="fuel-daily-tooltip"><div class="fuel-daily-tooltip-title">${dayLabel}</div>${medianLine}${rows}</div>`;
        },
      },
    };
  }, [
    dailyFuelEfficiencyChart.assets,
    dailyFuelEfficiencyChart.chartRangeEnd,
    dailyFuelEfficiencyChart.chartRangeStart,
    dailyFuelEfficiencyChart.yMax,
    fuelEfficiencyZoomRange.max,
    fuelEfficiencyZoomRange.min,
    fuelSignalFilterRange.end,
    fuelSignalFilterRange.start,
  ]);

  const handleFuelEfficiencyResetZoom = () => {
    setFuelEfficiencyZoomRange({ min: null, max: null });
    const chart = fuelEfficiencyChartRef.current?.chart;
    const rangeStart = dailyFuelEfficiencyChart.chartRangeStart ?? fuelSignalFilterRange.start;
    const rangeEnd = dailyFuelEfficiencyChart.chartRangeEnd ?? fuelSignalFilterRange.end;
    if (chart && rangeStart != null && rangeEnd != null) {
      chart.zoomX(rangeStart, rangeEnd);
      return;
    }
    chart?.resetSeries();
  };

  const fuelConsumptionChartOptions = useMemo(() => ({
    chart: {
      id: 'daily-fuel-consumption-chart',
      type: 'bar',
      stacked: true,
      toolbar: { show: false },
      fontFamily: 'Inter',
      animations: { enabled: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: filterDayCount > 20 ? '85%' : '55%',
        borderRadius: 4,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      type: 'datetime',
      min: fuelSignalFilterRange.start ?? undefined,
      max: fuelSignalFilterRange.end ?? undefined,
      labels: {
        formatter: (value) => moment(value).format('MM-DD'),
      },
    },
    yaxis: {
      title: { text: 'Fuel (L)' },
      min: 0,
      labels: {
        formatter: (value) => Number(value).toFixed(0),
      },
    },
    colors: DAILY_FUEL_ASSET_COLORS,
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'left',
    },
    tooltip: {
      shared: true,
      intersect: false,
      custom({ series, dataPointIndex, w }) {
        const dayTs = w.globals.seriesX[0]?.[dataPointIndex];
        const dayLabel = dayTs ? moment(dayTs).format('YYYY-MM-DD') : '';
        let total = 0;
        const rows = w.globals.seriesNames.map((name, index) => {
          const val = Number(series[index]?.[dataPointIndex]) || 0;
          if (val <= 0) return '';
          total += val;
          const color = w.globals.colors[index];
          return `<div class="fuel-daily-tooltip-row"><span class="fuel-daily-tooltip-dot" style="background:${color}"></span>${name}: ${val.toFixed(1)} L</div>`;
        }).filter(Boolean).join('');
        return `<div class="fuel-daily-tooltip"><div class="fuel-daily-tooltip-title">${dayLabel} · total ${total.toFixed(1)} L</div>${rows}</div>`;
      },
    },
  }), [filterDayCount, fuelSignalFilterRange.end, fuelSignalFilterRange.start]);

  const energyChartOptions = {
    chart: {
      id: 'energy-consumption-chart',
      type: 'line',
      toolbar: { show: false },
      fontFamily: 'Inter',
      animations: {
        enabled: false,
      },
    },
    stroke: {
      curve: 'smooth',
      width: 2,
      colors: ['#175CD3'],
    },
    xaxis: {
      type: 'datetime',
      labels: {
        formatter: (value) => moment(value).format('MMM DD, h:mm A'),
      },
    },
    yaxis: {
      title: {
        text: 'Energy (kWh)',
      },
      min: 0,
      labels: {
        formatter: (value) => value.toFixed(2),
      },
    },
    tooltip: {
      x: {
        formatter: (value) => moment(value).format('MMM DD, YYYY h:mm A'),
      },
      y: {
        formatter: (value) => Number(value).toFixed(2),
      },
    },
    colors: ['#175CD3'],
  };

  const getMaxValue = (data, key) => Math.max(...data.map((item) => item[key]), 0);
  const getBarWidth = (value, max) => (max > 0 ? `${(value / max) * 100}%` : '0%');

  // Map asset name -> { fleetClass, fuelType } from devices + breakdown row profile fields.
  const assetMetaByName = useMemo(
    () => mergeTableRowMetaIntoMap(buildAssetMetaMap(listAsset), fuelData.tableData),
    [listAsset, fuelData.tableData],
  );

  // Enrich each breakdown row with fleet class, badges and backend RM costs.
  const enrichedTableData = useMemo(
    () => fuelData.tableData.map(
      (row) => enrichRowWithCosts(row, lookupAssetMeta(assetMetaByName, row.asset), tariffRateByCode, fuelPriceByCode),
    ),
    [fuelData.tableData, assetMetaByName, tariffRateByCode, fuelPriceByCode],
  );

  // Fuel (ICE / Hybrid) and Energy (EV / Hybrid) row sets — kept separate so
  // litres and kWh are never aggregated together.
  const fuelRows = useMemo(
    () => {
      let rows = enrichedTableData.filter((row) => isFuelClass(row.fleetClass));
      if (selectedAssetName) {
        rows = rows.filter((row) => row.asset === selectedAssetName);
      }
      return rows;
    },
    [enrichedTableData, selectedAssetName],
  );

  const dailyFuelConsumptionChart = useMemo(() => {
    const tz = displayTimezone;
    const fuelAssetNames = new Set(
      requestAssets
        .filter((asset) => isFuelClass(classifyFleet(asset.assetType)))
        .map((asset) => asset.id),
    );
    const assetSeries = (fuelData.perAssetFuelConsumption || []).filter(
      (entry) => fuelAssetNames.has(entry.asset),
    );

    if (assetSeries.length === 0) {
      return { series: [], dayCount: 0, assets: [] };
    }

    return buildDailyFuelConsumptionByAsset(
      assetSeries,
      tz,
      fuelSignalFilterRange.start,
      fuelSignalFilterRange.end,
    );
  }, [
    fuelData.perAssetFuelConsumption,
    fuelSignalFilterRange.end,
    fuelSignalFilterRange.start,
    requestAssets,
    displayTimezone,
  ]);

  const baseEnergyRows = useMemo(
    () => {
      let rows = enrichedTableData.filter((row) => isEnergyClass(row.fleetClass));
      if (selectedAssetName) {
        rows = rows.filter((row) => row.asset === selectedAssetName);
      }
      return rows;
    },
    [enrichedTableData, selectedAssetName],
  );

  const displayTotalEnergyKpi = useMemo(
    () => resolveTotalEnergyKpi(
      fuelData.kpis.totalEnergyConsumption,
      baseEnergyRows,
      fuelData.energyChart,
    ),
    [fuelData.kpis.totalEnergyConsumption, fuelData.energyChart, baseEnergyRows],
  );

  const mileageKmByAsset = useMemo(() => {
    const map = {};
    fuelRows.forEach((row) => {
      const km = Number(row.mileageKm);
      if (Number.isFinite(km) && km > 0) {
        map[row.asset] = km;
      }
    });
    return map;
  }, [fuelRows]);

  const energyRows = useMemo(() => {
    const selectedMileageKm = selectedAssetName
      && Number(fuelData.kpis.mileage.value) > 0
      ? Number(fuelData.kpis.mileage.value)
      : null;

    return enrichEnergyRowsWithAggregate(baseEnergyRows, displayTotalEnergyKpi.value)
      .map((row) => recalculateEnergyRowMetrics(row, tariffRateByCode, {
        mileageFallbackKm: selectedMileageKm,
        mileageKmFromFuelRow: mileageKmByAsset[row.asset],
      }));
  }, [
    baseEnergyRows,
    displayTotalEnergyKpi.value,
    tariffRateByCode,
    selectedAssetName,
    fuelData.kpis.mileage.value,
    mileageKmByAsset,
  ]);

  const selectedFleetClasses = useMemo(() => {
    const normalizedSelectedAsset = String(selectedAssetName || '').toLowerCase();
    const capabilities = (fuelData.assetCapabilities || []).filter((capability) => (
      !selectedAssetName
      || String(capability.asset || '').toLowerCase() === normalizedSelectedAsset
    ));
    if (capabilities.length > 0) {
      const apiFleetClasses = capabilities
        .map((capability) => capability.fleetClass)
        .filter(Boolean);
      if (apiFleetClasses.length > 0) return apiFleetClasses;
    }

    const rows = enrichedTableData.filter((row) => (
      !selectedAssetName
      || String(row.asset || '').toLowerCase() === normalizedSelectedAsset
    ));
    if (rows.length > 0) return rows.map((row) => row.fleetClass);

    const fallbackAssets = selectedAssetName ? [assetSelectionList] : requestAssets;
    return fallbackAssets.map((asset) => classifyFleet(asset?.assetType));
  }, [
    assetSelectionList,
    enrichedTableData,
    fuelData.assetCapabilities,
    requestAssets,
    selectedAssetName,
  ]);

  const { showFuelSection, showEnergySection } = resolveSectionVisibility(
    fleetFilter,
    selectedFleetClasses,
  );

  const idleWasteSpecIncomplete = useMemo(
    () => hasIncompleteIdleWasteSpec(fuelRows),
    [fuelRows],
  );

  const maxTotalFuel = getMaxValue(fuelRows, 'totalFuelConsumption');
  const maxFuelEfficiency = getMaxValue(fuelRows, 'fuelEfficiency');

  const fuelSubtotal = useMemo(() => ({
    totalFuelConsumption: sumField(fuelRows, 'totalFuelConsumption'),
    tripFuelCostRM: sumField(fuelRows, 'tripFuelCostRM'),
    idleWasteCostRM: sumField(fuelRows, 'idleWasteCostRM'),
  }), [fuelRows]);

  const energySubtotal = useMemo(() => ({
    energyConsumption: sumField(energyRows, 'energyConsumption'),
    energyCostRM: sumField(energyRows, 'energyCostRM'),
  }), [energyRows]);

  // Energy KPI / table data comes from the same getFuelManagement payload.
  const energyDataAvailable = useMemo(
    () => displayTotalEnergyKpi.hasValue
      || energyRows.some((row) => row.energyConsumption !== null || row.energyCostRM !== null),
    [displayTotalEnergyKpi, energyRows],
  );

  const comparisonDays = useMemo(() => {
    if (date.length < 2) return 7;
    const startDate = moment(date[0]?.toDate?.() || date[0]).startOf('day');
    const endDate = moment(date[1]?.toDate?.() || date[1]).startOf('day');
    if (!startDate.isValid() || !endDate.isValid()) return 7;
    return Math.abs(endDate.diff(startDate, 'days')) + 1;
  }, [date]);

  const rowsPerPage = 5;
  const sortedFuelRows = useMemo(
    () => {
      const copy = [...fuelRows];
      copy.sort((a, b) => compareFuelTableRows(a, b, tableSortColumn, tableSortOrder));
      return copy;
    },
    [fuelRows, tableSortColumn, tableSortOrder],
  );

  const handleFuelTableSort = (column) => {
    setCurrentTablePage(1);
    if (tableSortColumn === column) {
      setTableSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setTableSortColumn(column);
      setTableSortOrder('desc');
    }
  };
  const totalTablePages = Math.max(1, Math.ceil(sortedFuelRows.length / rowsPerPage));
  const paginatedFuelRows = sortedFuelRows.slice(
    (currentTablePage - 1) * rowsPerPage,
    currentTablePage * rowsPerPage,
  );

  useEffect(() => {
    setCurrentTablePage(1);
  }, [fuelData.tableData, fleetFilter, selectedAssetName]);

  const renderSortableHeader = (column, label) => (
    <th aria-sort={fuelTableAriaSort(column, tableSortColumn, tableSortOrder)}>
      <button
        type="button"
        className="fuel-table-sortable-th"
        onClick={() => handleFuelTableSort(column)}
      >
        {label}
        <span className={fuelTableSortArrowClass(column, tableSortColumn)}>
          {fuelTableSortArrowChar(column, tableSortColumn, tableSortOrder)}
        </span>
      </button>
    </th>
  );

  // Hybrid assets can carry both a fuel-type and a charging-tariff badge (§2.4).
  const renderAssetCell = (row) => (
    <td className="asset-cell">
      <div className="asset-cell-inner">
        <span className="fuel-asset-name">{row.asset}</span>
        {row.fuelTypeLabel ? (
          <span className="fuel-type-badge">{row.fuelTypeLabel}</span>
        ) : null}
        {row.chargingTariffLabel ? (
          <span className="fuel-type-badge fuel-tariff-badge">{row.chargingTariffLabel}</span>
        ) : null}
      </div>
    </td>
  );

  const renderCostCell = (value) => {
    if (value === null || value === undefined) return <td>—</td>;
    return <td>{`RM ${formatMetricNumber(value)}`}</td>;
  };

  const renderPendingConfigCell = (value, hasVolume) => {
    if (value !== null && value !== undefined) return renderCostCell(value);
    if (hasVolume) return <td className="fuel-pending-cell">Pending config</td>;
    return <td>—</td>;
  };

  // ICE / Hybrid breakdown: Liters columns + RM fuel cost (when configured).
  const renderFuelSection = () => (
    <div className="fuel-analytics-table-section">
      <div
        className="fuel-analytics-table-header"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <h3 className="fuel-analytics-table-title">
          {fleetFilter === FLEET_FILTER.MIXED
            ? 'Fuel Efficiency Breakdown — ICE / Hybrid (Liters)'
            : 'Fuel Efficiency Breakdown Analysis'}
        </h3>
      </div>
      {idleWasteSpecIncomplete && (
        <p className="fuel-idle-waste-footnote">
          Idle waste may include excusable idle until device firmware supports finer motion states.
        </p>
      )}
      <table className="fuel-efficiency-table">
        <thead>
          <tr>
            <th>Asset</th>
            {renderSortableHeader('totalFuelConsumption', 'Total Fuel Consumption (L)')}
            {renderSortableHeader('tripFuelCostRM', 'Trip Fuel Cost (RM)')}
            {renderSortableHeader('idleWasteCostRM', 'Idle Waste Cost (RM)')}
            {renderSortableHeader('durationOn', 'Duration On [HH:MM:SS]')}
            {renderSortableHeader('idlingTime', 'Idling Time [HH:MM:SS]')}
            {renderSortableHeader('idlingRatio', 'Idling Ratio (%)')}
            {renderSortableHeader('fuelEfficiency', 'Fuel Efficiency (km/L)')}
          </tr>
        </thead>
        <tbody>
          {paginatedFuelRows.length === 0 ? (
            <tr>
              <td colSpan={8} className="fuel-empty-cell">
                No fuel (ICE / Hybrid) assets for this selection.
              </td>
            </tr>
          ) : paginatedFuelRows.map((row) => (
            <tr key={row.asset}>
              {renderAssetCell(row)}
              <td>
                <div className="bar-chart-cell">
                  <div className="bar-value">{formatMetricNumber(row.totalFuelConsumption)}</div>
                  <div className="bar-container">
                    <div
                      className="bar-bar bar-red"
                      style={{ width: getBarWidth(row.totalFuelConsumption, maxTotalFuel) }}
                    />
                  </div>
                </div>
              </td>
              {renderPendingConfigCell(
                row.tripFuelCostRM,
                row.totalFuelConsumption > 0,
              )}
              {renderPendingConfigCell(
                row.idleWasteCostRM,
                (row.idleWasteFuel ?? 0) > 0,
              )}
              <td>{row.durationOn}</td>
              <td>{row.idlingTime}</td>
              <td>
                {row.idlingRatio.toFixed(2)}
                %
              </td>
              <td>
                <div className="bar-chart-cell">
                  <div className="bar-value">{formatMetricNumber(row.fuelEfficiency)}</div>
                  <div className="bar-container">
                    <div
                      className="bar-bar bar-purple"
                      style={{ width: getBarWidth(row.fuelEfficiency, maxFuelEfficiency) }}
                    />
                  </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        {fuelRows.length > 0 && (
          <tfoot>
            <tr className="fuel-subtotal-row">
              <td className="asset-cell">Subtotal (Fuel)</td>
              <td>
                {Number.isFinite(fuelSubtotal.totalFuelConsumption)
                  ? `${formatMetricNumber(fuelSubtotal.totalFuelConsumption)} L`
                  : '—'}
              </td>
              <td>{formatSubtotalCostRM(fuelSubtotal.tripFuelCostRM, formatMetricNumber)}</td>
              <td>{formatSubtotalCostRM(fuelSubtotal.idleWasteCostRM, formatMetricNumber)}</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
            </tr>
          </tfoot>
        )}
      </table>
      <div className="fuel-analytics-pagination" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{`${currentTablePage}/${totalTablePages}`}</span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            aria-label="Previous page"
            onClick={() => setCurrentTablePage((prev) => Math.max(prev - 1, 1))}
            disabled={currentTablePage <= 1}
            style={{
              border: '1px solid #D0D5DD',
              background: currentTablePage <= 1 ? '#F9FAFB' : '#FFFFFF',
              color: currentTablePage <= 1 ? '#98A2B3' : '#344054',
              borderRadius: '8px',
              padding: '6px 12px',
              fontSize: '14px',
              cursor: currentTablePage <= 1 ? 'not-allowed' : 'pointer',
            }}
          >
            Back
          </button>
          <button
            type="button"
            aria-label="Next page"
            onClick={() => setCurrentTablePage((prev) => Math.min(prev + 1, totalTablePages))}
            disabled={currentTablePage >= totalTablePages}
            style={{
              border: '1px solid #D0D5DD',
              background: currentTablePage >= totalTablePages ? '#F9FAFB' : '#FFFFFF',
              color: currentTablePage >= totalTablePages ? '#98A2B3' : '#344054',
              borderRadius: '8px',
              padding: '6px 12px',
              fontSize: '14px',
              cursor: currentTablePage >= totalTablePages ? 'not-allowed' : 'pointer',
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );

  // EV / Hybrid breakdown: kWh + Energy Cost columns. Renders real values once
  // the backend supplies energy fields; otherwise shows a pending state.
  const renderEnergySection = () => (
    <div className="fuel-analytics-table-section">
      <div
        className="fuel-analytics-table-header"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <h3 className="fuel-analytics-table-title">
          {fleetFilter === FLEET_FILTER.MIXED
            ? 'Energy Breakdown — EV / Hybrid (kWh)'
            : 'Energy Efficiency Breakdown Analysis'}
        </h3>
      </div>
      <table className="fuel-efficiency-table">
        <thead>
          <tr>
            <th>Asset</th>
            <th>Total Energy (kWh)</th>
            <th>Energy Cost (RM)</th>
            <th>Duration On [HH:MM:SS]</th>
            <th>Idling Time [HH:MM:SS]</th>
            <th>Idling Ratio (%)</th>
            <th>Energy Efficiency (km/kWh)</th>
          </tr>
        </thead>
        <tbody>
          {energyRows.length === 0 ? (
            <tr>
              <td colSpan={7} className="fuel-empty-cell">
                No energy (EV / Hybrid) assets for this selection.
              </td>
            </tr>
          ) : energyRows.map((row) => (
            <tr key={row.asset}>
              {renderAssetCell(row)}
              <td>
                {row.energyConsumption !== null && row.energyConsumption >= 0
                  ? formatMetricNumber(row.energyConsumption)
                  : <span className="fuel-pending-cell">Pending</span>}
              </td>
              {renderCostCell(row.energyCostRM)}
              <td>{row.durationOn}</td>
              <td>{row.idlingTime}</td>
              <td>
                {row.idlingRatio.toFixed(2)}
                %
              </td>
              <td>
                {row.energyEfficiency !== null && row.energyEfficiency >= 0
                  ? formatMetricNumber(row.energyEfficiency)
                  : <span className="fuel-pending-cell">Pending</span>}
              </td>
            </tr>
          ))}
        </tbody>
        {energyRows.length > 0 && (
          <tfoot>
            <tr className="fuel-subtotal-row">
              <td className="asset-cell">Subtotal (Energy)</td>
              <td>
                {energyDataAvailable && Number.isFinite(energySubtotal.energyConsumption)
                  ? `${formatMetricNumber(energySubtotal.energyConsumption)} kWh`
                  : <span className="fuel-pending-cell">Pending</span>}
              </td>
              <td>{formatSubtotalCostRM(energySubtotal.energyCostRM, formatMetricNumber)}</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );

  return (
    <div className="fuel-dashboard-container">
    {/* <div className="fuel-analytics-chart-title"> */}
      <ListNavigationTab list={assetPerformanceNavigationList} navigtionClick={navigationClick} setNavigtionClick={setNavigationClick} />
      <div className="fuel-analytics-header">
        <div className="fuel-analytics-header-left">
          <h1 className="fuel-dashboard-main-title">Fuel Analysis</h1>
          <p className="fleet-dashboard-description">
          The fuel analysis tracks fuel efficiency over time. It breaks down total fuel consumption and mileage for both overall and individual assets.
          </p>
        </div>
        <div className="fuel-analytics-filters">
          <div className="fuel-fleet-filter" role="group" aria-label="Fleet filter">
            {FLEET_FILTER_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`fuel-fleet-filter-btn${fleetFilter === option.value ? ' fuel-fleet-filter-btn-active' : ''}`}
                aria-pressed={fleetFilter === option.value}
                onClick={() => setFleetFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="fuel-analytics-filter-box">
            <DateRangePicker
              date={date}
              setDate={setDate}
              inputValue={inputValue}
              setInputValue={setInputValue}
              minDateRange={minDateRange}
              setMinDateRange={setMinDateRange}
              maxDateRange={maxDateRange}
              setMaxDateRange={setMaxDateRange}
              setApplyDatePicker={() => {}}
              dateRange={dateRange}
              setDateRange={setDateRange}
            />
          </div>
          <div className="fuel-analytics-filter-box">
            <Autocomplete
              component="FuelAnalytics"
              autocompleteComponent="AssetSearch"
              list={listAsset}
              setSelectionList={setAssetSelectionList}
              selectionList={assetSelectionList}
              text="Select asset / device"
              isImage
              isAgencySelector={false}
              inputValue={inputSelectonText}
              stateInputValue={setInputSelectonText}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
              dropDownLoading={dropDownLoading}
              setDropDownLoading={setDropDownLoading}
              loadAsset={loadAsset}
            />
          </div>
        </div>
      </div>
      {(fuelFetchError || priceRegistryError) && (
        <div className="fuel-analytics-alert" role="alert">
          {fuelFetchError || priceRegistryError}
        </div>
      )}

      {loadFuel ? (
        <>
          <div className="fuel-analytics-kpi-section">
            <FuelKpiCardSkeleton />
            {showFuelSection && (
              <>
                <FuelKpiCardSkeleton />
                <FuelKpiCardSkeleton />
                <FuelKpiCardSkeleton />
              </>
            )}
            {showEnergySection && <FuelKpiCardSkeleton />}
          </div>
          <div className="fuel-analytics-charts-section">
            <div className="fuel-analytics-table-container">
              {showFuelSection && <FuelTableSkeleton columnCount={8} />}
              {showEnergySection && <FuelTableSkeleton columnCount={7} />}
            </div>
          </div>
          <div className="fuel-analytics-charts-section">
            {showFuelSection && (
              <>
                <FuelChartCardSkeleton titleWidth="40%" />
                <FuelChartCardSkeleton height={280} titleWidth="50%" />
                <FuelChartCardSkeleton titleWidth="45%" />
              </>
            )}
            {showEnergySection && <FuelChartCardSkeleton height={300} titleWidth="48%" />}
          </div>
        </>
      ) : (
        <>
          {/* KPI Cards Section - Top. Fuel (L) and Energy (kWh) are kept as
              separate cards and never combined into one total. */}
          <div className="fuel-analytics-kpi-section">
            <Card
              title="Mileage (km)"
              body={formatMetricNumber(fuelData.kpis.mileage.value || 0)}
              {...toKpiPercentChangeProps(fuelData.kpis.mileage)}
              comparisonDays={comparisonDays}
              width="100%"
            />
            {showFuelSection && (
              <Card
                title="Total Fuel Consumption (L)"
                body={formatMetricNumber(fuelData.kpis.totalFuelConsumption.value, 2)}
                {...toKpiPercentChangeProps(fuelData.kpis.totalFuelConsumption)}
                comparisonDays={comparisonDays}
                width="100%"
              />
            )}
            {showFuelSection && (
              <Card
                title="Average Fuel Consumption (L/hr)"
                body={formatMetricNumber(fuelData.kpis.avgFuelConsumption.value || 0)}
                {...toKpiPercentChangeProps(fuelData.kpis.avgFuelConsumption)}
                comparisonDays={comparisonDays}
                width="100%"
              />
            )}
            {showFuelSection && (
              <Card
                title="Average Fuel Efficiency (km/L)"
                body={formatMetricNumber(fuelData.kpis.avgFuelEfficiency.value)}
                {...toKpiPercentChangeProps(fuelData.kpis.avgFuelEfficiency)}
                comparisonDays={comparisonDays}
                width="100%"
              />
            )}
            {showEnergySection && (
              <Card
                title="Total Energy Consumption (kWh)"
                body={formatMetricNumber(displayTotalEnergyKpi.value || 0)}
                {...toKpiPercentChangeProps(displayTotalEnergyKpi)}
                comparisonDays={comparisonDays}
                width="100%"
              />
            )}
            {showEnergySection && fuelData.kpis.avgEnergyEfficiency.hasValue && (
              <Card
                title="Average Energy Efficiency (km/kWh)"
                body={formatMetricNumber(fuelData.kpis.avgEnergyEfficiency.value || 0)}
                {...toKpiPercentChangeProps(fuelData.kpis.avgEnergyEfficiency)}
                comparisonDays={comparisonDays}
                width="100%"
              />
            )}
          </div>

          {/* Main Content Area — breakdown tables. Fuel (Liters) and Energy (kWh)
              are rendered as separate sections so the two units are never combined. */}
          <div className="fuel-analytics-charts-section">
            <div className="fuel-analytics-table-container">
              {showFuelSection && renderFuelSection()}
              {showEnergySection && renderEnergySection()}
            </div>
          </div>

          {showFuelSection && selectedAssetName && (
            <div className="fuel-analytics-charts-section">
              <div className="fuel-analytics-table-container">
                <FuelLevelSignalPanel
                  signalView={fuelSignalView}
                  loading={loadFuelSignal}
                  error={fuelSignalError}
                  emptyReason={fuelSignalEmptyReason}
                  assetName={selectedAssetName}
                  fuelType={assetSelectionList?.fuelType || null}
                  fuelPriceRows={fuelPriceRowsForEvents}
                  fuelPriceByCode={fuelPriceByCode}
                  timezone={displayTimezone}
                  filterRangeStart={fuelSignalFilterRange.start}
                  filterRangeEnd={fuelSignalFilterRange.end}
                />
              </div>
            </div>
          )}

          {/* Charts Section */}
          <div className="fuel-analytics-charts-section">
            {showFuelSection && dailyFuelEfficiencyChart.series.length > 0 && (
            <div className="fuel-analytics-table-container">
            <div className="fuel-analytics-chart-container">
              <div className="fuel-analytics-chart-header-row">
                <div>
                  <h3 className="fuel-analytics-chart-title">Daily fuel efficiency</h3>
                  <p className="fuel-analytics-chart-subtitle">
                    km/L per day · bold = fleet median · fuel assets only
                  </p>
                </div>
                {isFuelEfficiencyZoomed && (
                  <div className="fuel-analytics-chart-zoom-controls">
                    <span className="fuel-analytics-chart-zoom-range">
                      Showing
                      {' '}
                      {formatDailyChartRangeDate(fuelEfficiencyZoomRange.min)}
                      {' '}
                      –
                      {' '}
                      {formatDailyChartRangeDate(fuelEfficiencyZoomRange.max)}
                    </span>
                    <button
                      type="button"
                      className="fuel-analytics-chart-reset-zoom"
                      onClick={handleFuelEfficiencyResetZoom}
                    >
                      Reset zoom
                    </button>
                  </div>
                )}
              </div>
              <div className="fuel-analytics-chart-toolbar">
                <span className="fuel-analytics-chart-hint">Drag on the chart to zoom by time</span>
                {!isFuelEfficiencyZoomed
                  && fuelSignalFilterRange.start != null
                  && fuelSignalFilterRange.end != null && (
                  <span className="fuel-analytics-chart-zoom-range">
                    Date range:
                    {' '}
                    {formatDailyChartRangeDate(fuelSignalFilterRange.start)}
                    {' '}
                    –
                    {' '}
                    {formatDailyChartRangeDate(fuelSignalFilterRange.end)}
                  </span>
                )}
              </div>
              <div className="fuel-analytics-chart">
                <ReactApexChart
                  ref={fuelEfficiencyChartRef}
                  options={fuelEfficiencyChartOptions}
                  series={dailyFuelEfficiencyChart.series}
                  type="line"
                  height={320}
                />
              </div>
            </div>
            </div>
            )}
            {showFuelSection && (
            <div className="fuel-analytics-table-container">
            <div className="fuel-analytics-chart-container">
              <FleetEventTimelinePanel
                timelineView={fleetTimelineView}
                loading={loadFleetTimeline}
                error={fleetTimelineError}
                timezone={displayTimezone}
                filterRangeStart={fuelSignalFilterRange.start}
                filterRangeEnd={fuelSignalFilterRange.end}
                onAssetClick={handleFleetTimelineAssetClick}
                emptyMessage="Select a vehicle to load its refill, drain, and alert timeline."
              />
            </div>
            </div>
            )}
            {showFuelSection && dailyFuelConsumptionChart.series.length > 0 && (
            <div className="fuel-analytics-table-container">
            <div className="fuel-analytics-chart-container">
              <h3 className="fuel-analytics-chart-title">Daily consumption by vehicle</h3>
              <p className="fuel-analytics-chart-subtitle">
                stacked fuel litres per day • EVs excluded (fuel only)
              </p>
              <div className="fuel-analytics-chart">
                <ReactApexChart
                  options={fuelConsumptionChartOptions}
                  series={dailyFuelConsumptionChart.series}
                  type="bar"
                  height={320}
                />
              </div>
            </div>
            </div>
            )}
            {showEnergySection && fuelData.energyChart.length > 0 && (
            <div className="fuel-analytics-table-container">
            <div className="fuel-analytics-chart-container">
              <h3 className="fuel-analytics-chart-title">Energy Consumption with Time</h3>
              <div className="fuel-analytics-chart">
                <ReactApexChart
                  options={energyChartOptions}
                  series={[{ name: 'Energy (kWh)', data: fuelData.energyChart }]}
                  type="line"
                  height={300}
                />
              </div>
            </div>
            </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
